<script setup lang="ts">
  import { ref, watch, PropType, computed, nextTick, onUnmounted } from 'vue';
  import SearchIcon from '@/shared/assets/icons/SearchIcon.svg';
  import { IconImage, SelectInput } from '@/shared/ui';
  import { useDocumentSearchTypeStore } from '@/shared/lib';

  const SEARCH_DEBOUNCE_MS = 300;

  const props = defineProps({
    searchFields: { type: Array as PropType<{ id: string; name: string }[]>, default: () => [] },
    initialSearchTerm: { type: String, default: '' },
    branch: String,
    independentSearchField: { type: Boolean, default: false },
    clearSearchOnFieldChange: { type: Boolean, default: true },
    debounceMs: { type: Number, default: SEARCH_DEBOUNCE_MS },
    disabled: { type: Boolean, default: false },
  });

  const searchFieldId = defineModel<string>('searchFieldId', { default: 'prefix' });

  const searchTerm = ref(props.initialSearchTerm);
  const searchInputRef = ref<HTMLInputElement | null>(null);
  const docStore = useDocumentSearchTypeStore();

  const emit = defineEmits(['search']);

  let debounceTimeout: ReturnType<typeof setTimeout> | null = null;

  // A term written by the parent (`initialSearchTerm`) or by the field change below is already
  // known to whoever listens, so it must not come back as a `search` event: that echo made every
  // screen load its list twice. Only what the user types is emitted.
  let silentTerm: string | null = null;
  const setTermSilently = (value: string) => {
    if (debounceTimeout) clearTimeout(debounceTimeout);
    silentTerm = value;
    searchTerm.value = value;
  };

  const selectSearchField = (value: string) => {
    const previous = activeSearchField.value;
    if (props.independentSearchField) {
      searchFieldId.value = value;
    } else {
      docStore.setSearchType(value as 'name' | 'prefix');
    }
    if (value === previous) return;

    if (props.clearSearchOnFieldChange) {
      setTermSilently('');
      emit('search', '');
    } else if (searchTerm.value.trim() !== '') {
      if (debounceTimeout) clearTimeout(debounceTimeout);
      emit('search', searchTerm.value);
    }

    nextTick(() => {
      searchInputRef.value?.focus();
    });
  };

  // Only the user's own choice in this bar changes the search: the store is shared by every screen
  // and is also hydrated from the URL, and neither of those is a request to search again.
  const activeSearchField = computed({
    get: () => (props.independentSearchField ? searchFieldId.value : docStore.searchType.value),
    set: selectSearchField,
  });

  watch(searchTerm, (newVal: string) => {
    if (silentTerm !== null && newVal === silentTerm) {
      silentTerm = null;
      return;
    }
    silentTerm = null;
    if (debounceTimeout) clearTimeout(debounceTimeout);

    debounceTimeout = setTimeout(() => {
      emit('search', newVal);
    }, props.debounceMs);
  });

  onUnmounted(() => {
    if (debounceTimeout) clearTimeout(debounceTimeout);
  });

  watch(
    () => props.initialSearchTerm,
    (newVal: string) => {
      setTermSilently(newVal);
    },
    { immediate: true }
  );
</script>
<template>
  <div class="flex flex-row items-center bg-white-01 border border-gray-02 rounded-lg h-[42px]">
    <div class="flex-none w-[200px] h-full border-r border-gray-02">
      <SelectInput
        :options="props.searchFields"
        :model-value="activeSearchField"
        :disabled="props.disabled"
        container-class="h-full"
        button-class="!border-none !rounded-none !rounded-l-lg h-full py-2 px-4"
        arrow-right="right-4"
        @update:model-value="activeSearchField = $event"
      />
    </div>

    <div class="flex-1 flex items-center gap-3 pl-4 pr-4">
      <IconImage
        :src="SearchIcon"
        alt="Search"
        class="w-5 h-5 flex-none text-gray-02"
      />
      <input
        id="search-term"
        ref="searchInputRef"
        v-model="searchTerm"
        type="text"
        placeholder="Buscar"
        :disabled="props.disabled"
        class="flex-1 text-sm font-normal text-gray-07 placeholder:text-gray-03 focus:outline-none bg-transparent disabled:cursor-not-allowed disabled:placeholder:text-gray-16"
      >
    </div>
  </div>
</template>
